<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { dynamicTime } from 'shared/helpers/timeHelper';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import KanbanMenu from './KanbanMenu.vue';
import { isWaitingTooLong } from '../helpers/kanbanHelpers';

const props = defineProps({
  conversation: { type: Object, required: true },
});

const emit = defineEmits(['action']);

const store = useStore();
const { t } = useI18n();
const accountLabels = useMapGetter('labels/getLabels');

const sender = computed(() => props.conversation.meta?.sender || {});
const assignee = computed(() => props.conversation.meta?.assignee || null);
const unreadCount = computed(() => props.conversation.unread_count || 0);

// inboxes/getInbox devolve snake_case, que é o formato que o ChannelIcon espera
const inbox = computed(() =>
  props.conversation.inbox_id
    ? store.getters['inboxes/getInbox'](props.conversation.inbox_id)
    : {}
);

// conversation.labels é um array de STRINGS (títulos) — resolvemos a cor pelo store
const labels = computed(() => {
  const titles = new Set(props.conversation.labels || []);
  if (!titles.size) return [];
  return (accountLabels.value || []).filter(label => titles.has(label.title));
});

const waitingLabel = computed(() => {
  const since = props.conversation.waiting_since;
  return since ? dynamicTime(since) : '';
});

const waitingIsHigh = computed(() => isWaitingTooLong(props.conversation));

const menuItems = computed(() => [
  {
    label: t('KANBAN.CARD.ACTIONS.OPEN'),
    value: 'open',
    action: 'open',
    icon: 'i-lucide-message-square',
  },
  {
    label: t('KANBAN.CARD.ACTIONS.ASSIGN_TO_ME'),
    value: 'self-assign',
    action: 'self-assign',
    icon: 'i-lucide-user-check',
  },
  {
    label: t('KANBAN.CARD.ACTIONS.RESOLVE'),
    value: 'resolve',
    action: 'resolve',
    icon: 'i-lucide-check-circle-2',
  },
  {
    label: t('KANBAN.CARD.ACTIONS.VIEW_CONTACT'),
    value: 'contact',
    action: 'contact',
    icon: 'i-lucide-user',
  },
]);
</script>

<template>
  <article
    class="group/card relative flex flex-col gap-2 p-3 rounded-lg cursor-pointer bg-n-background outline outline-1 outline-n-container transition-[transform,box-shadow,outline-color] duration-150 ease-out hover:-translate-y-0.5 hover:shadow-md hover:outline-n-brand"
  >
    <div class="flex items-start gap-2 min-w-0">
      <Avatar
        :src="sender.thumbnail"
        :name="sender.name || t('KANBAN.CARD.UNKNOWN')"
        :size="28"
        rounded-full
      />
      <div class="flex flex-col flex-1 min-w-0">
        <span class="text-sm font-medium truncate text-n-slate-12">
          {{ sender.name || t('KANBAN.CARD.UNKNOWN') }}
        </span>
        <span
          v-if="sender.phone_number"
          class="text-xs truncate text-n-slate-11 tabular-nums"
        >
          {{ sender.phone_number }}
        </span>
      </div>
      <CardPriorityIcon
        v-if="conversation.priority"
        :priority="conversation.priority"
        class="mt-0.5"
      />
      <KanbanMenu
        :menu-items="menuItems"
        :trigger-label="t('KANBAN.CARD.ACTIONS.MENU')"
        trigger-class="opacity-0 group-hover/card:opacity-100 focus-within:opacity-100 transition-opacity"
        @action="payload => emit('action', { ...payload, conversation })"
      />
    </div>

    <div v-if="labels.length" class="flex flex-wrap gap-1">
      <span
        v-for="label in labels.slice(0, 3)"
        :key="label.id"
        class="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded-md bg-n-alpha-2 text-n-slate-11 max-w-[9rem]"
      >
        <span
          class="size-1.5 rounded-full shrink-0"
          :style="{ backgroundColor: label.color }"
        />
        <span class="truncate">{{ label.title }}</span>
      </span>
      <span
        v-if="labels.length > 3"
        class="px-1.5 py-0.5 text-xs rounded-md bg-n-alpha-2 text-n-slate-11"
      >
        +{{ labels.length - 3 }}
      </span>
    </div>

    <div class="flex items-center gap-2 mt-0.5">
      <ChannelIcon
        v-if="inbox && inbox.id"
        :inbox="inbox"
        class="size-3.5 text-n-slate-10"
        :title="inbox.name"
      />
      <span
        v-if="waitingLabel"
        class="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded-md transition-colors"
        :class="
          waitingIsHigh
            ? 'bg-n-ruby-3 text-n-ruby-11 font-medium'
            : 'bg-transparent text-n-slate-10'
        "
        :title="t('KANBAN.CARD.WAITING_TOOLTIP')"
      >
        <span
          class="i-lucide-clock size-3"
          :class="{ 'animate-pulse': waitingIsHigh }"
        />
        {{ waitingLabel }}
      </span>
      <span
        v-if="unreadCount"
        class="px-1.5 ms-auto text-xs font-medium rounded-full bg-n-brand text-white"
      >
        {{ unreadCount > 9 ? '9+' : unreadCount }}
      </span>
      <Avatar
        v-if="assignee"
        :src="assignee.thumbnail"
        :name="assignee.name"
        :size="18"
        rounded-full
        :class="unreadCount ? 'ms-1' : 'ms-auto'"
        :title="assignee.name"
      />
    </div>
  </article>
</template>
